import {
  marketRegionMemberRoleLabels,
  marketRegionScopeTypeLabels,
  marketRegionTypeLabels,
} from "../config";
import type { MarketRegionMemberRole, MarketRegionScopeItem, MarketRegionScopeType, MarketRegionType } from "../types";

export interface MarketRegionScopeSummaryGroup {
  key: string;
  title: string;
  hasWholeInclude: boolean;
  includedItems: string[];
  excludedItems: string[];
}

/** 格式化区域类型展示文案。 */
export function formatMarketRegionType(type: MarketRegionType | string) {
  return marketRegionTypeLabels[type as MarketRegionType] ?? type;
}

/** 格式化区域范围类型展示文案。 */
export function formatMarketRegionScopeType(type: MarketRegionScopeType | string) {
  return marketRegionScopeTypeLabels[type as MarketRegionScopeType] ?? type;
}

/** 格式化区域成员角色展示文案。 */
export function formatMarketRegionMemberRole(role: MarketRegionMemberRole | string) {
  return marketRegionMemberRoleLabels[role as MarketRegionMemberRole] ?? role;
}

/** 组装区域范围在列表中的优先展示名称。 */
export function buildScopeDisplayName(scope: MarketRegionScopeItem) {
  return (
    scope.scopeNameSnapshot ||
    scope.city ||
    scope.province ||
    scope.orgUnitId?.toString() ||
    scope.orgId?.toString() ||
    scope.customerId?.toString() ||
    scope.contactId?.toString() ||
    "-"
  );
}

/** 将范围列表整理成按省份/范围聚合的树状表达。 */
export function buildMarketRegionScopeSummary(scopes: MarketRegionScopeItem[]): MarketRegionScopeSummaryGroup[] {
  const groups = new Map<string, MarketRegionScopeSummaryGroup>();

  for (const scope of scopes) {
    const title = buildScopeSummaryGroupTitle(scope);
    const group = getOrCreateScopeSummaryGroup(groups, title);
    const isWholeProvinceInclude = scope.scopeType === "province" && Boolean(scope.province) && !scope.isExcluded;
    if (isWholeProvinceInclude) {
      group.hasWholeInclude = true;
      continue;
    }
    const itemName = buildScopeSummaryItemName(scope);
    const targetItems = scope.isExcluded ? group.excludedItems : group.includedItems;
    if (itemName && !targetItems.includes(itemName)) {
      targetItems.push(itemName);
    }
  }

  return Array.from(groups.values()).filter(
    (group) => group.hasWholeInclude || group.includedItems.length > 0 || group.excludedItems.length > 0,
  );
}

/** 获取或创建范围摘要分组。 */
function getOrCreateScopeSummaryGroup(
  groups: Map<string, MarketRegionScopeSummaryGroup>,
  title: string,
): MarketRegionScopeSummaryGroup {
  const existingGroup = groups.get(title);
  if (existingGroup) {
    return existingGroup;
  }
  const group = {
    key: title,
    title,
    hasWholeInclude: false,
    includedItems: [],
    excludedItems: [],
  };
  groups.set(title, group);
  return group;
}

/** 按省份优先聚合范围摘要。 */
function buildScopeSummaryGroupTitle(scope: MarketRegionScopeItem) {
  return scope.province || scope.country || buildScopeDisplayName(scope);
}

/** 生成范围摘要二级节点名称。 */
function buildScopeSummaryItemName(scope: MarketRegionScopeItem) {
  if (scope.scopeType === "province") {
    return scope.province || scope.scopeNameSnapshot || "";
  }
  if (scope.scopeType === "city") {
    return scope.city || scope.scopeNameSnapshot || "";
  }
  return buildScopeDisplayName(scope);
}
